import { useCallback, useEffect, useState, type ReactNode } from 'react'
import { Link, NavLink, useNavigate, useSearchParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import { ChevronDown, Download, House as HouseIcon, ListChecks, LogOut, MessageSquareQuote, Plus, Refrigerator, Settings, X } from 'lucide-react'
import { useHouseholds, useLogout, useMe, useResendVerification } from '@/api/hooks'
import { cn } from '@/lib/utils'
import { recallHousehold } from '@/lib/household-memory'
import { useApiErrorText } from '@/lib/error-text'
import { Button, IconButton } from '@/components/ui/button'
import { ActionSheet } from '@/components/ui/sheet'
import { StatusBadge } from '@/components/ui/status-badge'
import { useToast } from '@/components/ui/toast'
import { NotificationBell } from '@/features/notifications/notification-bell'
import { ChromeProvider, ScreenHeader } from './app-chrome'
import { LumoraSignature } from './signature'

function useNavItems(householdId: string | undefined) {
  const { t } = useTranslation()
  if (!householdId) return []
  return [
    { to: `/h/${householdId}`, label: t('nav.home'), icon: ListChecks, end: true },
    { to: `/h/${householdId}/assets`, label: t('nav.assets'), icon: Refrigerator, end: false },
    { to: `/h/${householdId}/requests`, label: t('nav.requests'), icon: MessageSquareQuote, end: false },
    { to: `/h/${householdId}/house`, label: t('nav.house'), icon: HouseIcon, end: false },
  ]
}

/** Tab-root leading control: current household name; opens a sheet to switch or create one. */
function HouseholdSwitcher({ currentId, large = false }: { currentId: string | undefined; large?: boolean }) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const households = useHouseholds()
  const list = households.data?.data ?? []
  const current = list.find((h) => h.id === currentId)
  const [open, setOpen] = useState(false)
  return (
    <ActionSheet
      open={open}
      onOpenChange={setOpen}
      title={t('household.switcher.label')}
      {...(households.isError ? { description: t('errors.generic') } : {})}
      trigger={
        large ? (
          // Large-title variant (home): the heading's accessible name stays the household name.
          <button type="button" aria-haspopup="dialog" className="-ml-1 inline-flex max-w-full items-center gap-1.5 rounded-control px-1 text-left transition-colors duration-fast active:bg-border-subtle/60">
            <span className="min-w-0 break-words">{current?.name ?? t('household.switcher.choose')}</span>
            <ChevronDown aria-hidden className="size-6 shrink-0 text-fg-muted" />
            <span className="sr-only">, {t('household.switcher.label')}</span>
          </button>
        ) : (
        <button
          type="button"
          aria-label={`${t('household.switcher.label')}: ${current?.name ?? t('household.switcher.choose')}`}
          className="inline-flex min-h-11 min-w-0 max-w-[62vw] items-center gap-1 rounded-control px-2 text-title font-semibold text-fg transition-colors duration-fast active:bg-border-subtle/60 sm:max-w-xs"
        >
          <HouseIcon aria-hidden className="size-5 shrink-0 text-action" />
          <span className="truncate">{current?.name ?? t('household.switcher.choose')}</span>
          <ChevronDown aria-hidden className="size-4 shrink-0 text-fg-muted" />
        </button>
        )
      }
      actions={[
        ...list.map((h) => ({ label: h.name, icon: HouseIcon, checked: h.id === currentId, onSelect: () => navigate(`/h/${h.id}`) })),
        { label: t('household.new.menu'), icon: Plus, onSelect: () => navigate('/households/new') },
      ]}
    />
  )
}

/** Avatar button: account sheet (who is signed in, settings, install, sign out). */
function ProfileButton() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const me = useMe().data
  const logout = useLogout()
  const initial = (me?.name ?? '?').trim().charAt(0).toLocaleUpperCase()
  return (
    <ActionSheet
      title={t('nav.account')}
      header={
        <div className="mb-3 flex items-center gap-3 px-1">
          <span aria-hidden className="inline-flex size-12 items-center justify-center rounded-full bg-action text-title font-semibold text-on-action">{initial}</span>
          <span className="min-w-0">
            <span className="block truncate text-body font-semibold">{me?.name}</span>
            <span className="block truncate text-small text-fg-muted">{me?.email}</span>
          </span>
        </div>
      }
      trigger={
        <button type="button" aria-label={t('nav.profile')} className="inline-flex size-11 items-center justify-center rounded-full transition-colors duration-fast active:bg-border-subtle/60">
          <span aria-hidden className="inline-flex size-8 items-center justify-center rounded-full bg-action text-small font-semibold text-on-action">{initial}</span>
        </button>
      }
      actions={[
        { label: t('nav.settings'), icon: Settings, onSelect: () => navigate('/settings') },
        { label: t('pwa:install.title'), icon: Download, onSelect: () => navigate('/install') },
        { label: t('nav.signOut'), icon: LogOut, tone: 'danger', onSelect: () => logout.mutate(undefined, { onSettled: () => navigate('/login', { replace: true }) }) },
      ]}
    />
  )
}

/**
 * Header for the four tab roots and other top-level screens: household switcher on the left,
 * notifications + account on the right, large title in the content.
 */
export function TabScreenHeader({ title, subtitle, actions, householdId, switcherAsTitle = false }: { title: string; subtitle?: ReactNode; actions?: ReactNode; householdId?: string | undefined; switcherAsTitle?: boolean }) {
  const effectiveId = householdId ?? recallHousehold() ?? undefined
  return (
    <ScreenHeader
      large
      title={title}
      {...(subtitle ? { subtitle } : {})}
      {...(switcherAsTitle ? { titleContent: <HouseholdSwitcher currentId={effectiveId} large /> } : {})}
      leading={switcherAsTitle ? <Link to="/" className="inline-flex min-h-11 items-center px-2 no-underline lg:hidden"><LumoraSignature /></Link> : <HouseholdSwitcher currentId={effectiveId} />}
      actions={
        <>
          {actions}
          <NotificationBell />
          <ProfileButton />
        </>
      }
    />
  )
}

/** Unverified-address banner with resend; handles 429. */
export function VerifyEmailBanner() {
  const { t } = useTranslation()
  const me = useMe().data
  const resend = useResendVerification()
  const errorText = useApiErrorText()
  const toast = useToast()
  if (!me || me.email_verified) return null
  return (
    <div className="mx-4 mt-3 rounded-card bg-warning-bg px-4 py-3 text-warning sm:mx-6 lg:mx-8">
      <div className="mx-auto flex max-w-page flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="space-y-1">
          <StatusBadge tone="warning">{t('verify.banner.badge')}</StatusBadge>
          <p className="text-small">{t('verify.banner.text', { email: me.email })}</p>
          {resend.isError ? (
            <p role="alert" className="text-small font-medium">{errorText(resend.error)}</p>
          ) : null}
          {resend.isSuccess ? <p role="status" className="text-small font-medium">{t('verify.banner.sent')}</p> : null}
        </div>
        <Button
          variant="secondary"
          loading={resend.isPending}
          onClick={() => resend.mutate(undefined, { onSuccess: () => toast.show({ title: t('verify.banner.sent') }) })}
        >
          {t('verify.banner.resend')}
        </Button>
      </div>
    </div>
  )
}

/** `?oauth=signed_in|linked` confirmation after returning from Google. */
function OAuthNotice() {
  const { t } = useTranslation()
  const [params, setParams] = useSearchParams()
  const value = params.get('oauth')
  const [shown, setShown] = useState<string | null>(() => (value === 'signed_in' || value === 'linked' ? value : null))
  useEffect(() => {
    // Remove the one-shot marker from the URL; the message stays until dismissed.
    if (value) {
      const next = new URLSearchParams(params)
      next.delete('oauth')
      setParams(next, { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value])
  if (!shown) return null
  return (
    <div role="status" className="mx-4 mt-3 rounded-card bg-success-bg px-4 py-2 text-success sm:mx-6 lg:mx-8">
      <div className="mx-auto flex max-w-page items-center justify-between gap-3">
        <p className="text-small font-medium">{t(`settings.oauth.notice.${shown}`)}</p>
        <IconButton aria-label={t('common.close')} onClick={() => setShown(null)}>
          <X aria-hidden className="size-4" />
        </IconButton>
      </div>
    </div>
  )
}

export function AppShell({ householdId, children }: { householdId?: string | undefined; children: ReactNode }) {
  const { t } = useTranslation()
  const effectiveId = householdId ?? recallHousehold() ?? undefined
  const items = useNavItems(effectiveId)
  const [slot, setSlot] = useState<HTMLElement | null>(null)
  // Counter, not boolean: during a route change the next screen may mount before the previous unmounts.
  const [hiders, setHiders] = useState(0)
  const onTabBarHidden = useCallback((hidden: boolean) => setHiders((n) => Math.max(0, n + (hidden ? 1 : -1))), [])
  const showTabs = items.length > 0 && hiders === 0

  return (
    <ChromeProvider slot={slot} onTabBarHidden={onTabBarHidden}>
      <div className="min-h-dvh lg:flex">
        {items.length > 0 ? (
          <nav aria-label={t('nav.main')} className="hidden w-60 shrink-0 border-r border-border-subtle bg-surface lg:block">
            <div className="sticky top-0 flex flex-col gap-1 p-4">
              <Link to="/" className="mb-4 inline-flex min-h-11 items-center px-3">
                <LumoraSignature />
              </Link>
              {items.map(({ to, label, icon: Icon, end }) => (
                <NavLink
                  key={to}
                  to={to}
                  end={end}
                  className={({ isActive }) =>
                    cn(
                      'flex min-h-11 items-center gap-3 rounded-control px-3 text-body font-medium text-fg',
                      isActive ? 'bg-action/12 text-action' : 'hover:bg-border-subtle/50',
                    )
                  }
                >
                  <Icon aria-hidden className="size-5" />
                  {label}
                </NavLink>
              ))}
            </div>
          </nav>
        ) : null}

        <div className="flex min-w-0 flex-1 flex-col">
          {/* Top app bar: each screen portals its content here (ScreenHeader). */}
          <header ref={setSlot} className="sticky top-[env(safe-area-inset-top)] z-30 min-h-14 border-b border-border-subtle bg-background/90 backdrop-blur-md" />
          <OAuthNotice />
          <VerifyEmailBanner />
          <main
            id="main"
            tabIndex={-1}
            className={cn(
              'mx-auto w-full max-w-page flex-1 px-4 pt-4 sm:px-6 lg:px-8 lg:pb-10',
              showTabs ? 'pb-[calc(var(--bottom-nav-height)+env(safe-area-inset-bottom)+1.5rem)]' : 'pb-[max(1.5rem,env(safe-area-inset-bottom))]',
            )}
          >
            {children}
          </main>
        </div>

        {showTabs ? (
          <nav aria-label={t('nav.main')} className="pb-safe fixed inset-x-0 bottom-0 z-30 border-t border-border-subtle bg-surface/95 backdrop-blur-md lg:hidden">
            <ul className="mx-auto flex max-w-lg">
              {items.map(({ to, label, icon: Icon, end }) => (
                <li key={to} className="flex-1">
                  <NavLink
                    to={to}
                    end={end}
                    className={({ isActive }) =>
                      cn(
                        'flex min-h-14 flex-col items-center justify-center gap-0.5 px-0.5 pt-1 font-medium no-underline [overflow-wrap:normal]',
                        isActive ? 'text-action' : 'text-fg-muted',
                      )
                    }
                  >
                    {({ isActive }) => (
                      <>
                        <span className={cn('inline-flex h-7 w-14 items-center justify-center rounded-full transition-colors duration-fast', isActive && 'bg-action/15')}>
                          <Icon aria-hidden className="size-[1.375rem]" strokeWidth={isActive ? 2.25 : 1.75} />
                        </span>
                        <span className="max-w-full truncate text-[0.6875rem] leading-4">{label}</span>
                      </>
                    )}
                  </NavLink>
                </li>
              ))}
            </ul>
          </nav>
        ) : null}
      </div>
    </ChromeProvider>
  )
}
